import { Stack, Text } from '@mantine/core'
import { useMemo } from 'react'

import { formatTime, parseTranscriptSegments, transcriptHasSpeakerLabels } from '@/utils/transcript'

export interface TranscriptViewProps {
  content: string
  /** Cap the number of rendered segments (e.g. for compact previews). */
  maxSegments?: number
  /** Render all text muted, matching a disabled/read-only preview look. */
  dimmed?: boolean
  className?: string
  style?: React.CSSProperties
}

/**
 * Renders a speaker-labeled transcript as readable lines:
 *   `00:04 Abhijeet Joshi (speaker 0): Let's learn...` (named)
 *   `00:04 Speaker 0: Let's learn...`                  (un-named)
 *
 * Falls back to plain text when the content has no speaker markers
 * (e.g. YouTube captions, PDFs).
 */
export function TranscriptView(props: TranscriptViewProps) {
  const { content, maxSegments, dimmed, className, style } = props

  // When dimmed, every part (including the normally-emphasized name/body) reads
  // muted so the whole block looks like a disabled preview.
  const mutedColor = dimmed ? 'dimmed' : undefined

  const segments = useMemo(
    () => (transcriptHasSpeakerLabels(content) ? parseTranscriptSegments(content) : []),
    [content],
  )

  // No markers (YouTube captions, PDFs) or a marker that didn't parse into
  // segments: show the raw text rather than an empty block.
  if (segments.length === 0) {
    return (
      <Text size="sm" c={mutedColor} className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>
        {content}
      </Text>
    )
  }

  const visible = maxSegments != null ? segments.slice(0, maxSegments) : segments
  const truncated = maxSegments != null && segments.length > maxSegments

  return (
    <Stack gap="xs" className={className} style={style}>
      {visible.map(seg => (
        <Text key={seg.id} size="sm" c={mutedColor}>
          <Text span c="dimmed" ff="monospace">
            {formatTime(seg.timeSec)}{' '}
          </Text>
          {seg.name ? (
            <>
              <Text span fw={600} c={mutedColor}>
                {seg.name}{' '}
              </Text>
              <Text span c="dimmed">
                (speaker {seg.speakerNumber}):{' '}
              </Text>
            </>
          ) : (
            <Text span fw={600} c={mutedColor}>
              Speaker {seg.speakerNumber}:{' '}
            </Text>
          )}
          {seg.text}
        </Text>
      ))}
      {truncated && (
        <Text size="sm" c="dimmed" fs="italic">
          …
        </Text>
      )}
    </Stack>
  )
}
